import React, { useEffect } from "react";
import { Redirect, withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { parse } from "query-string";

import I18n from "../../I18n";

import { mapProperties, hydrateRoute, number, parseFilter } from "colbi_web_ui/lib/utils";
import {
    logout,
    pushModal,
    pushDialog,
    popModal,
    uploadAction,
    toggleMenu,
    setLocale,
    auditNoteSubmit,
    removeModal,
    getTranslationTableDatasetLocaleAndEntities,
    deleteTranslationTableAction,
} from "../../store/actions";

import { LoadingIndicator, rows, DataGrid } from "colbi_web_ui/lib/components";
import { SovosPageLayout, SovosPageContentGeneric } from "mosaic-react";

import styles from "./SettingsWithDataGrid.module.sass";

import {
    decodeUserSession,
    buildStaticLinks,
    redirectNotValid,
    BuildSovosNavProps,
} from "../../utils/utils";
import GlobalLayout from "../../components/GlobalLayout/GlobalLayout";
import { useAuth } from "react-oidc-context";
import TranslationTableFormButton from "../../components/TranslationTableFormButton/TranslationTableFormButton";
import { generateSettingsMenuItems } from "../../components/GlobalLayout/menuBuilderUtils";

const SettingsWithDataGrid = (props) => {
    const { user, fetch, match, setLocale, location, history } = props;

    const locale = match.params.locale || "en";
    const i18n = I18n.use(locale);
    const auth = useAuth();

    const headerTitle =
        match.params.section === "kpis"
            ? `settings_menu_${match?.params?.section || ""}`
            : `${match?.params?.section || ""}`;

    useEffect(() => {
        setLocale(locale);
    }, [locale, setLocale]);

    const userInformation = decodeUserSession(user);

    const menuItems = generateSettingsMenuItems(userInformation?.role, history, locale, i18n) || [];
    
    const navigation = menuItems;

    const staticLinks = buildStaticLinks(navigation, history, match.params, i18n);

    const redirect = redirectNotValid(userInformation, locale, true, auth);

    if (redirect) {
        return redirect;
    }

    if (!(userInformation && userInformation.accessSettings)) {
        return <Redirect to={`/${locale}/unauthorized`} />;
    }

    (navigation || []).forEach((elem) => {
        elem.name = I18n.translate`${elem.name}`;
        return elem;
    });

    const queryParams = parse(location.search) || {};
    const routeParams = match.params || {};
    const params = {
        ...queryParams,
        ...routeParams,
    };

    const data = mapProperties(
        {
            id: "results",
            card: rows.CellCard,
            checkbox: true,
            items: "query(0).list",
            header: "query(0).header",
            footer: "query(0).footer",
            metadata: "query(0).metadata",
            page: "param(page)",
            status: "status(0)",
            pageSize: "param(pageSize)",
            pageDefault: 10,
            filter: "param(filter)",
            sort: "param(sort)",
            availableFilters: "query(0).filters",
            wrapper: "wrapper",
            showCheckbox: "query(0).showCheckbox",
            total: "query(0).metadata.total",
            basePath: `${match.path}/builder`,
        },
        (fetch || {}).results,
        (fetch || {}).status,
        (SettingsWithDataGrid.queries && SettingsWithDataGrid.queries(params)) || [],
        props,
        params,
        i18n,
        locale,
    );

    const currentSection = match.params.section || "/";

    if (currentSection === "/") {
        const basePath = "/settings/:organizationId?";
        const hydrated = hydrateRoute(basePath, { ...params });
        return <Redirect to={`${hydrated}${navigation[0]?.section || 'translations_table'}`} replace />;
    }

    const navProps = BuildSovosNavProps(
        undefined,
        [],
        { name: "" },
        {},
        userInformation,
        staticLinks || [],
    );

    const globalLayoutProps = {
        ...props,
        navProps,
        downloadableQueries: "",
        params,
        staticLinks,
        styles,
    };

    return (
        <GlobalLayout {...globalLayoutProps}>
            <SovosPageLayout 
                pageTitle={i18n`${headerTitle}`}
                titleBarActions={[<TranslationTableFormButton />]}
            >
                <SovosPageContentGeneric sx={{ padding: '0 !important', marginLeft: '24px', marginRight: '24px' }}>
                    <>
                        {fetch.activity && (
                            <LoadingIndicator className={`${styles["loading-indicator"]}`} />
                        )}
                        <DataGrid {...data} />
                    </>
                </SovosPageContentGeneric>
            </SovosPageLayout>
        </GlobalLayout>
    );
};

SettingsWithDataGrid.queries = (args) => {
    const { section, pageSize, page, filter, locale } = args || {};
    const pagesWithLocale = ["translations_table"];

    return section
        ? [
              {
                  resource: section,
                  args: {
                      pageSize: number(pageSize),
                      page: page ? number(page) : 0,
                      locale: pagesWithLocale.includes(section) ? locale : undefined,
                      filter: filter
                          ? { type: "[FilterInput!]", value: parseFilter(filter) }
                          : undefined,
                      sort: args.sort
                          ? { type: "[SortInput!]", value: parseFilter(args.sort) }
                          : undefined,
                  },
                  body: `__typename, items{...${section}},hasMore,total,filters{ prop, name, type, list, disableOperators }`,
              },
          ]
        : [];
};

export default connect(
    ({ user, fetch, menu, uploads }) => ({
        user,
        fetch,
        menu,
        uploads,
    }),
    {
        toggleMenu,
        logout,
        setLocale,
        pushModal,
        popModal,
        removeModal,
        pushDialog,
        uploadFiles: uploadAction,
        submitAuditNote: auditNoteSubmit,
        getTranslationTableDatasetLocaleAndEntities,
        deleteTranslationTableAction,
    },
)(withRouter(SettingsWithDataGrid));
